import Vue from 'vue';
import Router, { Route, RouteConfig } from 'vue-router';
import Routes from '@/Routes';
import store from '@/store';
import User, { PassengerGroupPermissionType } from './models/User';
import { RolePermission } from './models/Role';
import { PermissionEnum } from './models/DestinationRole';
import Passenger from './components/Passenger/models/Passenger';

Vue.use(Router);

const router = new Router({
  mode: 'hash',
  routes: Routes.routes,
   scrollBehavior: (to, from, savedPosition) => {
    if (savedPosition) {
      return savedPosition;
    } else if (from.meta && from.meta.scrollTop === false) {
      return (to as any).position;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

router.beforeEach(async (to, from, next) => {
  if (to.matched.some(route => !route.meta.allowAnonymous)) {
    // route does not allow anonymous, check if user is authenticated
    if (!store.getters['auth/isAuthenticated']) {
      // user is not authenticated, redirect
      next({
        name: Routes.signInRoute.name,
        query: { redirect: to.fullPath }
      })
    } else {
      // user is authenticated, proceed to next
      let me = (store.state as any).user.me as User;
      const destinationId = Number(to.params.destinationId);
      const redirectRoutes = [
        Routes.peopleRoute,
        Routes.timetableListRoute,
        Routes.arrivalsDeparturesRoute,
        Routes.destinationTripListRoute,
        Routes.aboutRoute
      ];

      // Route is not destination specific, proceed to next
      if (!destinationId) {
        next();
      }
      
      if (routeRequiresPermissions(to)) {
        if (!me){
          console.log("Router: Current user hasn't been loaded yet, loading ...")
          await store.dispatch('user/getMe');
          me = (store.state as any).user.me as User;
          if (me){
            console.log("Current user loaded.")
          }
        }

        if (!me){
          console.warn(
            "[Timit] Navigation occurred to a route which requires permissions" +
            ", but current user info has not yet been loaded."
          );
          return;
        }
        if (hasPermissions(to)) {

          /* 
           * Ensure that route has correct destinationId if 
           * a user manually inputs passengerId into the URL
           * and previous passenger has a different main destination 
           * than the changed passengerId. 
           */
          if (to.name === Routes.editPeopleRoute.name && to.params.passengerId) {
            const passengerId = parseInt(to.params.passengerId, 10);
            const passenger: Passenger = store.getters["passenger/findPassengerById"]
              (to.params.passengerId);
            if (!passenger){
              console.warn("Passenger was not found when processing nav guard!")
            }
            
            const passengerDestinationId = passenger ? passenger.destinationIds.find(x => x) : undefined;
            if (passengerDestinationId && passengerDestinationId !== destinationId) {
              next({
                name: Routes.editPeopleRoute.name,
                params: { destinationId: passengerDestinationId + '', passengerId: to.params.passengerId },
              });
            }
          }
          next();
        } else {

          // Redirect to allowed page
          const redirectRoute = redirectRoutes
          .find((r: RouteConfig) => {
            if (!r.meta) {
              return true;
            }
            if (r.meta.requirePassengerPermissions) {
              return store.getters['auth/hasPassengerPermission']
                (r.meta.requirePassengerPermissions, r)
            } else {
              return store.getters['auth/hasDestinationPermission']
                (to.params.destinationId, r.meta.requireDestinationPermissions)
            }
          });
          if (redirectRoute) {
            next({
              name: redirectRoute.name,
              params: { destinationId: to.params.destinationId }
            });
          }
        }
      // No permission required
      } else {
        next();
      }
    }
  } else {
    // route allows anonymous, proceed to next
    next()
  }
})

function routeRequiresPermissions(route: Route) {
  if (!route.meta) {
    return false;
  }
  return route.meta.requireDestinationPermissions || route.meta.requirePassengerPermissions;
}

function hasPermissions(to: Route) {

  const hasDestinationPermission = to.meta.requireDestinationPermissions 
    ? store.getters['auth/hasDestinationPermission'](to.params.destinationId, to.meta.requireDestinationPermissions) 
    : true;

  const hasPassengerPermission = to.meta.requirePassengerPermissions
    ? store.getters['auth/hasPassengerPermission'](to.meta.requirePassengerPermissions, to)
    : true;
  return hasDestinationPermission && hasPassengerPermission;
}

export default router;
